import { Outlet } from "react-router-dom";
import { AppMenu } from "./AppMenu";
import { useEffect, useRef, useState } from "react";
import { auth } from "shared/config/firebase";
import useAppStatus from "shared/store/appStatusStore";
import colorsStore from "shared/store/colorsStore";
import jwtAuthStore from "shared/store/jwtAuthStore";
import { debounce } from "utils/misc";
import { useTabletAndBelowMediaQuery } from "shared/store/responsiveStore";
import classNames from "classnames";
import styles from "./RootLayout.module.scss";

export const RootLayout: React.FC = () => {
  const colorsConfig = colorsStore((state) => state.colorsConfigStore);

  const [userLogged, setUserLogged] = useState<boolean>(false);
  const [isSmallScreen, setIsSmallScreen] = useState(false);

  const appStatus = useAppStatus((state) => state.appStatus);
  const setWindowW = useAppStatus((state) => state.setWindowW);

  const isTabletOrSmaller = useTabletAndBelowMediaQuery();

  const { shouldUseJwt, isVerified, jwtToken } = jwtAuthStore();

  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!shouldUseJwt) {
      return;
    }

    let isLoggedIn = false;
    if (isVerified && jwtToken) {
      isLoggedIn = true;
    }

    setUserLogged(isLoggedIn);
  }, [shouldUseJwt, isVerified, jwtToken]);

  useEffect(() => {
    if (!shouldUseJwt) {
      auth.onAuthStateChanged((user) => {
        setUserLogged(!!user);
      });
    }
    let debounceTimer;
    const handleResize = () => {
      if (debounceTimer) clearTimeout(debounceTimer);

      debounceTimer = setTimeout(() => {
        const width = window.innerWidth;
        const isSmall = appStatus === "idle" ? width < 650 : width < 1050;

        setWindowW(width);

        if (wrapperRef.current) {
          wrapperRef.current.style.overflowX = isSmall ? "auto" : "hidden";
        }
      }, 500);
    };
    setWindowW(window.innerWidth);

    // window.addEventListener("resize", debounce(handleResize, 500));
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [shouldUseJwt]);

  useEffect(() => {
    const width = window.innerWidth;
    const isSmall = appStatus === "idle" ? width < 650 : width < 1050;
    if (wrapperRef.current) {
      wrapperRef.current.style.overflowX = isSmall ? "auto" : "hidden";
    }
  }, [appStatus]);

  return (
    <div
      ref={wrapperRef}
      style={{
        position: "relative",
        display: "flex",
        flexDirection: isTabletOrSmaller ? "column" : "row",
        height: "100%",
        minWidth: "150px",
        width: "100%",
        alignItems: "stretch",
        justifyContent: "stretch",
        gap: 0,
        backgroundColor: colorsConfig.white,
        color: colorsConfig.finecoBlack,
      }}
    >
      {userLogged && (
        <div
          className={classNames({
            [styles.appMenuContainerDesktop]: !isTabletOrSmaller,
            [styles.appMenuContainerTablet]: isTabletOrSmaller,
          })}
        >
          <AppMenu />
        </div>
      )}

      <div
        className="flex h-full flex-col justify-stretch"
        style={{
          width: "100%",
          minWidth: "0", // This is crucial for flex item width handling
          flex: "1 1 auto", // Allow growing and shrinking
          position: "relative", // Create new positioning context
        }}
      >
        <div
          style={{
            position: "absolute",
            inset: "0",
            overflowX: "auto",
            overflowY: "auto",
          }}
        >
          <div
            style={{
              minWidth: "fit-content", // Ensure content doesn't get squished
              width: "100%", // Fill available space
              height: "100%",
            }}
          >
            <Outlet />
          </div>
        </div>
      </div>
    </div>
  );
};
